import { useQuery } from "@tanstack/react-query"
import { Download, Inbox } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useAuth } from "@/lib/auth"
import { formatDateLegible } from "@/lib/format"
import { t } from "@/lib/i18n"

import { descargarSustento, fetchSinComprobante } from "@/routes/review/api"

/**
 * LO QUE LLEGÓ SIN COMPROBANTE FISCAL — por correo o subido a mano.
 *
 * Un PDF sin su XML no es un comprobante que se pueda clasificar: no tiene
 * proveedor, ni importes, ni serie que leer sin interpretar. Hasta ahora quedaba
 * guardado como un nombre y una huella y no aparecía en NINGUNA pantalla: el
 * remitente creía haberlo mandado y nadie lo veía nunca.
 *
 * Aquí se ve, con quién lo mandó, y se puede abrir. Lo que hay que hacer con él
 * es una de dos: pedir el comprobante fiscal (el XML) o registrarlo a mano si es
 * de un proveedor que no emite electrónico (Adobe, Microsoft…). Cuando llega su
 * XML, el comprobante entra en la cola de arriba.
 */
export function SinComprobante() {
  const { activeTenantId, activeEntityId } = useAuth()
  const { toast } = useToast()
  const q = useQuery({
    queryKey: ["sustento-sin-comprobante", activeTenantId, activeEntityId],
    queryFn: () => fetchSinComprobante(activeEntityId ?? undefined),
    enabled: Boolean(activeTenantId),
  })
  const filas = q.data ?? []

  return (
    <WorkPanel
      title={t("Llegaron sin comprobante fiscal")}
      meta={
        <span className="flex items-center gap-1 text-2xs text-muted-foreground">
          <Inbox className="h-3 w-3" aria-hidden="true" />
          {filas.length} {t("PDF sin su XML · lo último, primero")}
        </span>
      }
      footnote={t("Un PDF sin su XML no se puede clasificar ni da crédito fiscal. Pide el XML al proveedor —al llegar, el comprobante entra en la cola de arriba— o regístralo a mano si el proveedor no emite comprobante electrónico.")}
    >
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={filas.length === 0}
        emptyMessage={t("No hay nada que llegara sin su comprobante.")}
        emptyHint={t("Lo que mandes por correo o subas sin su XML aparece aquí.")}
        onRetry={() => void q.refetch()}
        rows={3}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Archivo")}</TableHead>
              <TableHead>{t("De dónde llegó")}</TableHead>
              <TableHead>{t("Sociedad")}</TableHead>
              <TableHead>{t("Cuándo")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.map((f) => (
              <TableRow key={f.id}>
                <TableCell className="text-xs">{f.nombre_archivo}</TableCell>
                <TableCell className="text-2xs text-muted-foreground">
                  {f.canal === "correo" ? <Badge variant="outline" className="mr-1.5">{t("correo")}</Badge> : null}
                  {f.origen_ruta}
                </TableCell>
                <TableCell className="text-2xs">{f.sociedad ?? "—"}</TableCell>
                <TableCell className="num text-2xs text-muted-foreground">
                  {/* NO `toLocaleDateString()`: sigue al navegador, y «10/1/2026» es
                      el 1 de octubre o el 10 de enero según quién lo mire. */}
                  {f.creado_en ? formatDateLegible(f.creado_en) : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {f.archivado ? (
                    <Button
                      size="xs" variant="ghost"
                      onClick={() => descargarSustento(f.id, f.nombre_archivo).catch(() =>
                        toast({ variant: "destructive", title: t("No pude descargarlo") }))}
                    >
                      <Download aria-hidden="true" />
                      {t("Abrir")}
                    </Button>
                  ) : (
                    <span
                      className="text-2xs text-muted-foreground"
                      title={t("Llegó antes de que se guardaran los PDF: hay que volver a subirlo.")}
                    >
                      {t("sin archivo")}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataState>
    </WorkPanel>
  )
}
